import { useCallback, useEffect, useMemo } from 'react'
import { shallowEqual } from 'react-redux'

import { BlueprintDetailApiItem, coreApi } from '../../../api/core'
import { useAppDispatch, useAppSelector } from '../../../hooks/storeHooks'

export type BlueprintDetailResult = {
  blueprintId: string
  data?: BlueprintDetailApiItem
  isLoading: boolean
  // The request failed and there is no earlier payload to fall back on.
  isError?: boolean
}

export type BlueprintDetailsOptions = {
  // Required for PC/console game-level blueprints, which the endpoint otherwise rejects.
  includePcConsole?: boolean
}

// Detail requests allowed in flight at once per caller. A widely adopted family can
// name hundreds of game-level blueprints; the rest wait for a slot instead of
// reaching the backend in one burst.
export const MAX_CONCURRENT_BLUEPRINT_DETAIL_REQUESTS = 6

// Must match the args `useGetBlueprintQuery` builds, or the two subscribe to
// different cache entries for the same blueprint and fetch it twice.
const detailArgs = (blueprintFamilyId: string, blueprintId: string, includePcConsole?: boolean) =>
  includePcConsole ? { blueprintFamilyId, blueprintId, includePcConsole } : { blueprintFamilyId, blueprintId }

/**
 * Subscribes to the game-level blueprint detail (its events) for a variable
 * number of blueprints at once — a game can implement the same market
 * blueprint several times, and hooks can't be called in a loop. Uses the
 * endpoint's `initiate` / `select` pair so results share the normal RTK Query
 * cache with `useGetBlueprintQuery`. Nothing is fetched while `enabled` is false,
 * and at most `MAX_CONCURRENT_BLUEPRINT_DETAIL_REQUESTS` are in flight at a time;
 * queued blueprints report `isLoading` until their turn comes and resolves.
 */
export function useBlueprintDetails(
  blueprintFamilyId: string,
  blueprintIds: string[],
  enabled: boolean,
  { includePcConsole }: BlueprintDetailsOptions = {}
): BlueprintDetailResult[] {
  const dispatch = useAppDispatch()
  const idsKey = blueprintIds.join('|')
  const isActive = enabled && Boolean(blueprintFamilyId)

  useEffect(() => {
    if (!isActive || !idsKey) return
    const queue = idsKey.split('|')
    const subscriptions: { unsubscribe: () => void }[] = []
    let isCancelled = false
    const startNext = () => {
      if (isCancelled) return
      const blueprintId = queue.shift()
      if (blueprintId === undefined) return
      const subscription = dispatch(coreApi.endpoints.getBlueprint.initiate(detailArgs(blueprintFamilyId, blueprintId, includePcConsole)))
      subscriptions.push(subscription)
      // Settles on success and failure alike; either way the slot is free again.
      subscription.then(startNext, startNext)
    }
    for (let slot = 0; slot < MAX_CONCURRENT_BLUEPRINT_DETAIL_REQUESTS; slot++) startNext()
    return () => {
      isCancelled = true
      subscriptions.forEach((subscription) => subscription.unsubscribe())
    }
  }, [dispatch, blueprintFamilyId, idsKey, isActive, includePcConsole])

  const selectors = useMemo(
    () =>
      (idsKey ? idsKey.split('|') : []).map((blueprintId) => ({
        blueprintId,
        select: coreApi.endpoints.getBlueprint.select(detailArgs(blueprintFamilyId, blueprintId, includePcConsole)),
      })),
    [blueprintFamilyId, idsKey, includePcConsole]
  )

  return useAppSelector(
    (state) =>
      selectors.map(({ blueprintId, select }): BlueprintDetailResult => {
        const result = select(state)
        return {
          blueprintId,
          data: result.data,
          isLoading: isActive && !result.data && (result.isLoading || result.isUninitialized),
          isError: isActive && !result.data && result.isError,
        }
      }),
    // A fresh array every time; compare element-wise so unrelated store updates don't re-render.
    (a, b) => a.length === b.length && a.every((item, index) => shallowEqual(item, b[index]))
  )
}

/**
 * Re-requests the given game-level blueprint details, bypassing the cache. Adds
 * no subscription of its own: the entries stay alive through whichever
 * `useBlueprintDetails` caller is showing them, and every caller sees the result.
 */
export function useRetryBlueprintDetails(blueprintFamilyId: string, { includePcConsole }: BlueprintDetailsOptions = {}): (blueprintIds: string[]) => void {
  const dispatch = useAppDispatch()
  return useCallback(
    (blueprintIds: string[]) => {
      blueprintIds.forEach((blueprintId) => {
        dispatch(
          coreApi.endpoints.getBlueprint.initiate(detailArgs(blueprintFamilyId, blueprintId, includePcConsole), { subscribe: false, forceRefetch: true })
        )
      })
    },
    [dispatch, blueprintFamilyId, includePcConsole]
  )
}
